import { isSeatLocked } from "@components/table/members/assign-seats/assign-seats-utils";
import { SeatCheckbox } from "@components/table/members/assign-seats/seat-checkbox";
import { useAssignSeat } from "@components/table/members/assign-seats/use-assign-seat";
import { useMemberSeats } from "@components/table/members/assign-seats/use-member-seats";
import { useRemoveSceneSeats } from "@components/table/members/assign-seats/use-remove-scene-seats";
import { useSeatsAvailable } from "@components/table/members/assign-seats/use-seats-available";
import { CheckboxGroup, Dialog } from "@flat-ui";
import { getPrettyName } from "@utils/user-utils";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { Stack } from "@mui-wrapper";
import { useState } from "react";

/** The label of the action, so that whatever opens the dialog repeats what was clicked */
export const MANAGE_SEATS_TITLE = "Manage seats";
/**
 * Dialog that lets the user pick the seats of the workspace subscription that a member should hold.
 * Checking a seat gives it to the member, unchecking a SCENE seat they hold frees it again.
 *
 * Every seat the workspace offers is listed, including the ones that cannot be changed, so that the
 * user learns why instead of wondering where they went.
 */
export function ManageSeatsDialog({
  member,
  isOpen,
  onClose
}) {
  const {
    seatOptions
  } = useMemberSeats(member);
  const {
    assignSeat
  } = useAssignSeat();
  const {
    removeSceneSeats
  } = useRemoveSceneSeats();
  const seatsAvailable = useSeatsAvailable();

  // The seats the user checked or unchecked, which are shown as the member holds them otherwise
  const [checkedSeats, setCheckedSeats] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Only the seats that can still be changed count, so that a seat which changed meanwhile, e.g. by
  // the part of a save that succeeded, is not requested again
  const changedOptions = seatOptions.filter(option => !isSeatLocked(option) && isSeatChecked(option) !== option.isHeld);
  return <Dialog title={MANAGE_SEATS_TITLE} confirmText="Save" open={isOpen} onConfirm={saveSeats} showSpinner={isLoading} isConfirmDisabled={!changedOptions.length} onCancel={closeDialog} dataTestId="manage-seats-dialog">
      <CheckboxGroup label={<Stack component="span" sx={{
      gap: 1
    }}>
            Choose the seats for
            <Stack direction="row" component="span" sx={{
        alignItems: "center",
        gap: 1
      }}>
              <UserAvatarWithMenu member={member} />
              <strong>
                <var>{getPrettyName(member)}</var>
              </strong>
            </Stack>
          </Stack>}
    // The same space between the question and the seats as the dialog keeps around its title
    checkboxGroupSx={{
      gap: 1,
      mt: 2
    }}>
        {seatOptions.map(option_0 => <SeatCheckbox key={option_0.seat} option={option_0} isChecked={isSeatChecked(option_0)} seatsAvailable={seatsAvailable[option_0.seat]} onChange={(seat, isChecked) => setCheckedSeats(seats => ({
        ...seats,
        [seat]: isChecked
      }))} />)}
      </CheckboxGroup>
    </Dialog>;

  /** Whether the member should hold the seat once the dialog is saved */
  function isSeatChecked({
    seat: seat_0,
    isHeld
  }) {
    return checkedSeats[seat_0] ?? isHeld;
  }

  /** Closes the dialog without changing the seats of the member any further */
  function closeDialog() {
    setIsLoading(false);
    setCheckedSeats({});
    onClose();
  }

  /**
   * Gives the member the checked seats and frees the unchecked ones they hold. The dialog stays open
   * when one of them could not be changed, so that the user can try that one again.
   */
  async function saveSeats() {
    setIsLoading(true);

    // The seats do not depend on each other, so they are requested at the same time
    const results = await Promise.all(changedOptions.map(changeSeat));
    if (results.every(Boolean)) {
      closeDialog();
    } else {
      setIsLoading(false);
    }
  }

  /** Gives the member a seat they do not hold, or frees the SCENE seats they hold */
  function changeSeat({
    seat: seat_1,
    isHeld: isHeld_0
  }) {
    if (!isHeld_0) {
      return assignSeat(member, seat_1);
    }

    // Only the SCENE seat can be taken away, see getSeatOptions
    return removeSceneSeats(member);
  }
}